import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiDelete, apiFetch, apiPost, apiPut } from "@/lib/api";
import { folderKeys } from "@/lib/query";

interface FolderTemplate {
  id: string;
  name: string | null;
  description: string | null;
  isDefault: boolean | null;
  isActive: boolean | null;
}

interface FolderTemplatePath {
  id: string;
  folderTemplateId: string;
  fullPath: string | null;
  displayOrder: number | null;
  autoPopulateEntity: string | null;
  isRequired: boolean | null;
}

interface ProjectFolder {
  id: string;
  projectId: string;
  fullPath: string | null;
  displayOrder: number | null;
  isActive: boolean | null;
  isRequired: boolean | null;
  fileCount: number;
}

export type { FolderTemplate, FolderTemplatePath, ProjectFolder };

// ── Folder Templates ──

export function useFolderTemplates() {
  return useQuery({
    queryKey: folderKeys.templates,
    queryFn: () => apiFetch<FolderTemplate[]>("/folder-templates"),
  });
}

export function useCreateFolderTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<FolderTemplate>) =>
      apiPost<{ data: FolderTemplate }>("/folder-templates", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: folderKeys.templates });
      toast.success("Folder template created");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useUpdateFolderTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Partial<FolderTemplate>) =>
      apiPut<{ data: FolderTemplate }>(`/folder-templates/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: folderKeys.templates });
      toast.success("Folder template updated");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useSetDefaultFolderTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiPost<{ data: FolderTemplate }>(`/folder-templates/${id}/set-default`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: folderKeys.templates });
      toast.success("Default template updated");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

// ── Folder Template Paths ──

export function useFolderTemplatePaths(templateId: string) {
  return useQuery({
    queryKey: folderKeys.templatePaths(templateId),
    queryFn: () => apiFetch<FolderTemplatePath[]>(`/folder-templates/${templateId}/paths`),
    enabled: !!templateId,
  });
}

export function useCreateFolderTemplatePath(templateId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<FolderTemplatePath>) =>
      apiPost<{ data: FolderTemplatePath }>(`/folder-templates/${templateId}/paths`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: folderKeys.templatePaths(templateId) });
      toast.success("Template path created");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useUpdateFolderTemplatePath(templateId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pathId, ...data }: { pathId: string } & Partial<FolderTemplatePath>) =>
      apiPut<{ data: FolderTemplatePath }>(`/folder-templates/${templateId}/paths/${pathId}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: folderKeys.templatePaths(templateId) });
      toast.success("Template path updated");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useDeleteFolderTemplatePath(templateId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pathId: string) => apiDelete(`/folder-templates/${templateId}/paths/${pathId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: folderKeys.templatePaths(templateId) });
      toast.success("Template path deleted");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useReorderFolderTemplatePaths(templateId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { orderedIds: string[] }) =>
      apiPut<{ data: FolderTemplatePath[] }>(`/folder-templates/${templateId}/paths/reorder`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: folderKeys.templatePaths(templateId) });
      toast.success("Template paths reordered");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

// ── Project Folders ──

export function useProjectFolders(projectId: string) {
  return useQuery({
    queryKey: folderKeys.project(projectId),
    queryFn: () => apiFetch<ProjectFolder[]>(`/projects/${projectId}/folders`),
    enabled: !!projectId,
  });
}

export function useCreateProjectFolder(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<ProjectFolder>) =>
      apiPost<{ data: ProjectFolder }>(`/projects/${projectId}/folders`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: folderKeys.project(projectId) });
      toast.success("Folder created");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useRenameProjectFolder(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ folderId, name }: { folderId: string; name: string }) =>
      apiPut<{ data: ProjectFolder }>(`/projects/${projectId}/folders/${folderId}/rename`, {
        name,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: folderKeys.project(projectId) });
      toast.success("Folder renamed");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useDeactivateProjectFolder(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (folderId: string) =>
      apiPut<{ data: ProjectFolder }>(`/projects/${projectId}/folders/${folderId}/deactivate`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: folderKeys.project(projectId) });
      toast.success("Folder deactivated");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useInstantiateFolderTemplate(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (templateId?: string) =>
      apiPost<{ data: ProjectFolder[] }>(`/projects/${projectId}/folders/instantiate`, {
        templateId,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: folderKeys.project(projectId) });
      toast.success("Folder template applied");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}
